import React from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { FamilyThemeProvider } from './context/FamilyThemeContext';
import { AdminNotificationProvider } from './context/AdminNotificationContext';
import { FeatureGateProvider } from './components/FeatureGate';
import AuthCallback from './components/AuthCallback';
import { NativeDeepLinkListener } from './components/NativeDeepLinkListener';
import { PushRegistrar } from './components/PushRegistrar';
import AppReturn from './pages/AppReturn';
import ProtectedRoute from './components/ProtectedRoute';
import Landing from './pages/Landing';
import Features from './pages/Features';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import BecomePartner from './pages/BecomePartner';
import Dashboard from './pages/Dashboard';
import FamilyDetails from './pages/FamilyDetails';
import FamilyGallery from './pages/FamilyGallery';
import FamilySpiritual from './pages/FamilySpiritual';
import FamilyTimeline from './pages/FamilyTimeline';
import TimelineExport from './pages/TimelineExport';
import FamilyAnalytics from './pages/FamilyAnalytics';
import SharedView from './pages/SharedView';
import SharedAlbum from './pages/SharedAlbum';
import EventDetails from './pages/EventDetails';
import Messages from './pages/Messages';
import Notifications from './pages/Notifications';
import Resources from './pages/Resources';
import AdminProfessionals from './pages/AdminProfessionals';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentCancel from './pages/PaymentCancel';
import Achievements from './pages/Achievements';
import ConsultationDashboard from './pages/ConsultationDashboard';
import Subscription from './pages/Subscription';
import RoleGuide from './pages/RoleGuide';
import HelpCenter from './pages/HelpCenter';
import AcceptInvitation from './pages/AcceptInvitation';
import FeedbackDashboard from './pages/FeedbackDashboard';
import ProfessionalSignup from './pages/ProfessionalSignup';
import ProfessionalDirectory from './pages/ProfessionalDirectory';
import ProfessionalProfile from './pages/ProfessionalProfile';
import ProfessionalDashboard from './pages/ProfessionalDashboard';
import StripeSetupGuide from './pages/Admin/StripeSetupGuide';
import HealthCheck from './pages/HealthCheck';
import MyInvitations from './pages/MyInvitations';
import { Toaster } from 'sonner';
import { GlobalHelpButton } from './components/GlobalHelpButton';
import { FeedbackButton } from './components/FeedbackButton';
import { TouchTooltip } from './components/TouchTooltip';
import './App.css';

function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment (not query params) for session_id
  // This runs synchronously during render to prevent race conditions
  if (location.pathname === '/app-return') {
    return <AppReturn />;
  }
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/features" element={<Features />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/become-partner" element={<BecomePartner />} />
      <Route path="/accept-invitation" element={<AcceptInvitation />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            <Notifications />
          </ProtectedRoute>
        }
      />
      <Route
        path="/family/:familyId"
        element={
          <ProtectedRoute>
            <FamilyDetails />
          </ProtectedRoute>
        }
      />
      <Route
        path="/family/:familyId/gallery"
        element={
          <ProtectedRoute>
            <FamilyGallery />
          </ProtectedRoute>
        }
      />
      <Route
        path="/family/:familyId/spiritual"
        element={
          <ProtectedRoute>
            <FamilySpiritual />
          </ProtectedRoute>
        }
      />
      <Route
        path="/family/:familyId/timeline"
        element={
          <ProtectedRoute>
            <FamilyTimeline />
          </ProtectedRoute>
        }
      />
      <Route
        path="/family/:familyId/timeline/export"
        element={
          <ProtectedRoute>
            <TimelineExport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/event/:eventId"
        element={
          <ProtectedRoute>
            <EventDetails />
          </ProtectedRoute>
        }
      />
      <Route
        path="/messages"
        element={
          <ProtectedRoute>
            <Messages />
          </ProtectedRoute>
        }
      />
      <Route
        path="/messages/:conversationId"
        element={
          <ProtectedRoute>
            <Messages />
          </ProtectedRoute>
        }
      />
      <Route
        path="/resources"
        element={
          <ProtectedRoute>
            <Resources />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/professionals"
        element={
          <ProtectedRoute adminOnly>
            <AdminProfessionals />
          </ProtectedRoute>
        }
      />
      <Route
        path="/payment-success"
        element={<PaymentSuccess />}
      />
      <Route
        path="/payment-cancel"
        element={<PaymentCancel />}
      />
      <Route
        path="/family/:familyId/analytics"
        element={
          <ProtectedRoute>
            <FamilyAnalytics />
          </ProtectedRoute>
        }
      />
      <Route
        path="/shared/:shareToken"
        element={<SharedView />}
      />
      <Route
        path="/shared-album/:shareToken"
        element={<SharedAlbum />}
      />
      <Route
        path="/achievements"
        element={
          <ProtectedRoute>
            <Achievements />
          </ProtectedRoute>
        }
      />
      <Route
        path="/consultation-schedule"
        element={
          <ProtectedRoute>
            <ConsultationDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscription"
        element={
          <ProtectedRoute>
            <Subscription />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pricing"
        element={<Subscription isPublic={true} />}
      />
      <Route
        path="/role-guide"
        element={
          <ProtectedRoute>
            <RoleGuide />
          </ProtectedRoute>
        }
      />
      <Route
        path="/help-center"
        element={
          <ProtectedRoute>
            <HelpCenter />
          </ProtectedRoute>
        }
      />
      <Route
        path="/feedback-dashboard"
        element={
          <ProtectedRoute adminOnly>
            <FeedbackDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-invitations"
        element={
          <ProtectedRoute>
            <MyInvitations />
          </ProtectedRoute>
        }
      />
      <Route
        path="/professionals/signup"
        element={<ProfessionalSignup />}
      />
      <Route
        path="/pro"
        element={<ProfessionalSignup />}
      />
      <Route
        path="/professionals/directory"
        element={<ProfessionalDirectory />}
      />
      <Route
        path="/professionals/p/:profileId"
        element={<ProfessionalProfile />}
      />
      <Route
        path="/professionals/dashboard"
        element={
          <ProtectedRoute>
            <ProfessionalDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/stripe-setup"
        element={
          <ProtectedRoute adminOnly>
            <StripeSetupGuide />
          </ProtectedRoute>
        }
      />
      <Route
        path="/health-check"
        element={<HealthCheck />}
      />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <FamilyThemeProvider>
          <AdminNotificationProvider>
            <FeatureGateProvider>
              <NativeDeepLinkListener />
              <PushRegistrar />
              <AppRouter />
              <GlobalHelpButton />
              <FeedbackButton />
              <TouchTooltip />
              <Toaster richColors position="top-right" />
            </FeatureGateProvider>
          </AdminNotificationProvider>
        </FamilyThemeProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
